/**
 * The passkeys page's markup. The page owns the data and the calls; this owns
 * how each state looks, so the states render in a test without a server.
 */
import { Box, Heading, Stack, Text } from "@chakra-ui/react";
import { ReactNode } from "react";
import { TbKey, TbPlus } from "react-icons/tb";

import Card from "components/ds/Card";
import EmptyState from "components/ds/EmptyState";
import PloyButton from "components/ds/PloyButton";
import type { MFPasskey } from "state/members/passkeys";

import PasskeyRow from "./PasskeyRow";

export type PasskeysViewProps = {
  passkeys: MFPasskey[] | undefined;
  isLoading: boolean;
  loadError: string | null;
  now: number;
  /** The add form, when it is open (PasskeyEnrolment). */
  addSlot: ReactNode | null;
  /** The sign-in-again prompt, when a removal asked for one. */
  reauthSlot: ReactNode | null;
  removingId: string | null;
  onAdd: () => void;
  onRename: (passkey: MFPasskey, label: string) => Promise<void>;
  onRemove: (passkey: MFPasskey) => void;
};

const PasskeysView = ({
  passkeys,
  isLoading,
  loadError,
  now,
  addSlot,
  reauthSlot,
  removingId,
  onAdd,
  onRename,
  onRemove,
}: PasskeysViewProps) => {
  const list = passkeys ?? [];

  const body = (() => {
    if (isLoading) {
      return (
        <Text fontSize="smd" color="neutral.700" p="lg">
          Loading your passkeys.
        </Text>
      );
    }
    if (loadError) {
      return (
        <Text role="alert" fontSize="smd" color="error.700" p="lg">
          {loadError}
        </Text>
      );
    }
    if (list.length === 0) {
      return (
        <EmptyState
          icon={<TbKey />}
          title="No passkeys yet"
          body="Add one so you can sign access requests and approvals when your organisation asks."
          action={
            addSlot ? undefined : (
              <PloyButton
                variant="secondary"
                size="sm"
                onClick={onAdd}
                leftIcon={<TbPlus size={14} aria-hidden />}
              >
                Add a passkey
              </PloyButton>
            )
          }
        />
      );
    }
    return list.map((passkey) => (
      <PasskeyRow
        key={passkey.id}
        passkey={passkey}
        now={now}
        isRemoving={removingId === passkey.id}
        onRename={(label) => onRename(passkey, label)}
        onRemove={() => onRemove(passkey)}
      />
    ));
  })();

  return (
    <Box maxW="720px" mx="auto" w="full">
      <Stack spacing="lg">
        <Box py="lg">
          <Heading
            as="h1"
            m={0}
            fontFamily="heading"
            fontWeight={500}
            fontSize="3xl"
            lineHeight="1"
            letterSpacing="-0.01em"
            color="neutral.1000"
          >
            Passkeys
          </Heading>
          <Text fontSize="md" color="neutral.700" mt="md">
            Your organisation may ask you to sign access requests and approvals
            with a passkey. A passkey lives on your device or in your password
            manager, and you unlock it the way you unlock your device.
          </Text>
        </Box>

        {reauthSlot}

        <Card p={0} overflow="hidden">
          {body}
        </Card>

        {addSlot ? (
          <Card>
            <Stack spacing="md">
              <Text fontSize="md" fontWeight={600} color="neutral.900">
                Add a passkey
              </Text>
              {addSlot}
            </Stack>
          </Card>
        ) : (
          list.length > 0 && (
            <Box>
              <PloyButton
                variant="secondary"
                size="md"
                onClick={onAdd}
                leftIcon={<TbPlus size={14} aria-hidden />}
              >
                Add a passkey
              </PloyButton>
            </Box>
          )
        )}
      </Stack>
    </Box>
  );
};

export default PasskeysView;
